import React, { useState, useMemo, useCallback } from "react";
import { Button, Select, Switch, Tag, List } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, CheckOutlined } from "@ant-design/icons";
import { MessageCategory } from "../../../flatbuffers/generated/config/message-category";
import { OneHotExclusionConfigT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-config";
import { OneHotExclusionGroupT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-group";
import { OneHotExclusionEntryT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-entry";
import { MessageT } from "../../../flatbuffers/generated/ln2/message";
import {
  LN2_MESSAGE_CATEGORIES,
  Ln2MessageFilter,
  Ln2MessageCategory,
  buildMessageTForEnum,
  extractEnumValueFromMessageT,
} from "../../../flatbuffers/types/ln2MessageOptions";

export interface OneHotExclusionEditorProps {
  config: OneHotExclusionConfigT | null;
  onChange: (config: OneHotExclusionConfigT) => void;
  /** When set, only messages in the filter appear in the selector. */
  messageFilter?: Ln2MessageFilter;
  /** Show the groups with no way to change them. */
  readOnly?: boolean;
}

/**
 * Single O(1) lookup from MessageCategory → Ln2MessageCategory data.
 * Built once at module scope from the canonical LN2_MESSAGE_CATEGORIES array
 * by matching the enum ordinals (MessageCategory.Status=1 ↔ index 0, etc.).
 */
const CATEGORY_DATA_BY_MSG_CAT = new Map<MessageCategory, Ln2MessageCategory>(
  LN2_MESSAGE_CATEGORIES.map((cat, i) => [i + 1, cat] as [MessageCategory, Ln2MessageCategory]),
);

/** Reverse lookup: categoryKey string → MessageCategory enum value. */
const CATEGORY_KEY_TO_MSG_CAT = new Map<string, MessageCategory>(
  LN2_MESSAGE_CATEGORIES.map((cat, i) => [cat.categoryKey, (i + 1) as MessageCategory]),
);

function getEnumOptions(category: Ln2MessageCategory): { label: string; value: number }[] {
  const seen = new Set<number>();
  return category.options
    .filter((opt) => {
      if (seen.has(opt.enumValue)) return false;
      seen.add(opt.enumValue);
      return true;
    })
    .map((opt) => ({ label: opt.label, value: opt.enumValue }));
}

/**
 * Build a new OneHotExclusionGroupT using the FlatBuffer schema defaults
 * for the three behavior flags (preserve_positive=true,
 * evict_only_when_active=true, emit_deactivations=false). Wraps the
 * positional-boolean constructor so call sites stay named-argument
 * readable.
 */
function makeDefaultExclusionGroup(opts: {
  category: MessageCategory;
  categoryWide: boolean;
  entries: OneHotExclusionEntryT[];
}): OneHotExclusionGroupT {
  return new OneHotExclusionGroupT(
    opts.category,
    opts.categoryWide,
    opts.entries,
    true,
    true,
    false,
  );
}

function resolveEnumName(cat: MessageCategory, enumValue: number): string {
  if (enumValue < 0) return "Invalid";
  const catData = CATEGORY_DATA_BY_MSG_CAT.get(cat);
  if (!catData) return String(enumValue);
  const opt = catData.options.find((o) => o.enumValue === enumValue);
  return opt?.label ?? String(enumValue);
}

export default function OneHotExclusionEditor({
  config,
  onChange,
  messageFilter = undefined,
  readOnly = false,
}: OneHotExclusionEditorProps): React.ReactElement {
  const [pendingCategoryKey, setPendingCategoryKey] = useState<string>("status");
  const [pendingCategoryWide, setPendingCategoryWide] = useState<boolean>(false);
  const [pendingMembers, setPendingMembers] = useState<number[]>([]);
  // Editing state: null = adding new, {gi, ei} = editing an existing entry
  const [editing, setEditing] = useState<{ gi: number; ei: number } | null>(null);

  const groups = config?.groups ?? [];

  const availableCategories = useMemo(() => {
    if (!messageFilter) return LN2_MESSAGE_CATEGORIES;
    return LN2_MESSAGE_CATEGORIES.filter((cat) => messageFilter.has(cat.messageContent));
  }, [messageFilter]);

  const categoryOptions = useMemo(
    () => availableCategories.map((cat) => ({ label: cat.label, value: cat.categoryKey })),
    [availableCategories],
  );

  const messageOptions = useMemo(() => {
    const catData = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === pendingCategoryKey);
    if (!catData) return [];
    const options = getEnumOptions(catData);
    if (!messageFilter) return options;
    const allowed = messageFilter.get(catData.messageContent);
    if (!allowed) return [];
    return options.filter((opt) => allowed.has(opt.value));
  }, [pendingCategoryKey, messageFilter]);

  const handleAddOrUpdateGroup = (): void => {
    const msgCategory = CATEGORY_KEY_TO_MSG_CAT.get(pendingCategoryKey);
    if (msgCategory === undefined) return;

    // Dedup pendingMembers by enum value before building MessageT objects.
    // Antd Select with mode="multiple" usually dedupes, but legacy data
    // round-tripped from the server can contain duplicates which the user
    // can't fix through the dropdown alone.
    const uniqueMembers = Array.from(new Set(pendingMembers));
    const memberMessages = uniqueMembers
      .map((v) => buildMessageTForEnum(pendingCategoryKey, v))
      .filter((m): m is MessageT => m !== null);

    // Editing an existing entry — preserves the group's existing flags.
    if (editing) {
      const updatedGroups = groups.map((g, gi) => {
        if (gi !== editing.gi) return g;
        const cloned = g.clone();
        cloned.categoryWide = pendingCategoryWide;
        if (!pendingCategoryWide) {
          cloned.entries = cloned.entries.map((e, ei) =>
            ei === editing.ei ? new OneHotExclusionEntryT(memberMessages) : e,
          );
        }
        return cloned;
      });
      onChange(new OneHotExclusionConfigT(updatedGroups));
      setEditing(null);
      setPendingMembers([]);
      setPendingCategoryWide(false);
      return;
    }

    // Adding new
    const existing = groups.find((g) => g.category === msgCategory);
    let updatedGroups: OneHotExclusionGroupT[];

    if (existing) {
      const merged = existing.clone();
      if (pendingCategoryWide) {
        merged.categoryWide = true;
      } else {
        merged.entries = [...merged.entries, new OneHotExclusionEntryT(memberMessages)];
      }
      updatedGroups = groups.map((g) => (g.category === msgCategory ? merged : g));
    } else {
      const newGroup = makeDefaultExclusionGroup({
        category: msgCategory,
        categoryWide: pendingCategoryWide,
        entries: pendingCategoryWide ? [] : [new OneHotExclusionEntryT(memberMessages)],
      });
      updatedGroups = [...groups, newGroup];
    }

    onChange(new OneHotExclusionConfigT(updatedGroups));
    setPendingMembers([]);
    setPendingCategoryWide(false);
  };

  const handleToggleEvictOnlyWhenActive = useCallback(
    (groupIndex: number, value: boolean): void => {
      onChange(
        new OneHotExclusionConfigT(
          groups.map((g, gi) => {
            if (gi !== groupIndex) return g;
            const cloned = g.clone();
            cloned.evictOnlyWhenActive = value;
            return cloned;
          }),
        ),
      );
    },
    [groups, onChange],
  );

  const handleToggleEmitDeactivations = useCallback(
    (groupIndex: number, value: boolean): void => {
      onChange(
        new OneHotExclusionConfigT(
          groups.map((g, gi) => {
            if (gi !== groupIndex) return g;
            const cloned = g.clone();
            cloned.emitDeactivations = value;
            return cloned;
          }),
        ),
      );
    },
    [groups, onChange],
  );

  const handleEditEntry = useCallback(
    (gi: number, ei: number): void => {
      const group = groups[gi];
      if (!group) return;
      const catData = CATEGORY_DATA_BY_MSG_CAT.get(group.category);
      if (catData) setPendingCategoryKey(catData.categoryKey);
      setPendingCategoryWide(group.categoryWide);
      if (!group.categoryWide && group.entries[ei]) {
        // Dedup on edit-load so duplicates already saved on the server
        // surface as a single tag in the multi-select instead of two.
        const evs = group.entries[ei].members
          .map(extractEnumValueFromMessageT)
          .filter((ev) => ev >= 0);
        setPendingMembers(Array.from(new Set(evs)));
      }
      setEditing({ gi, ei });
    },
    [groups],
  );

  const handleCancelEdit = (): void => {
    setEditing(null);
    setPendingMembers([]);
    setPendingCategoryWide(false);
  };

  const handleDeleteGroup = (index: number): void => {
    onChange(new OneHotExclusionConfigT(groups.filter((_, i) => i !== index)));
  };

  const handleDeleteEntry = (groupIndex: number, entryIndex: number): void => {
    onChange(
      new OneHotExclusionConfigT(
        groups.map((g, gi) => {
          if (gi !== groupIndex) return g;
          const cloned = g.clone();
          cloned.entries = cloned.entries.filter((_, ei) => ei !== entryIndex);
          return cloned;
        }),
      ),
    );
  };

  return (
    <div style={{ padding: "8px 0" }}>
      <div
        style={{
          display: readOnly ? "none" : "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <Select
          size="small"
          style={{ width: 200 }}
          value={pendingCategoryKey}
          onChange={(v) => {
            setPendingCategoryKey(v);
            setPendingMembers([]);
          }}
          options={categoryOptions}
          placeholder="Category"
        />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12 }}>Category-wide</span>
          <Switch
            size="small"
            checked={pendingCategoryWide}
            onChange={(v) => setPendingCategoryWide(v)}
          />
        </div>
        {!pendingCategoryWide && (
          <Select
            mode="multiple"
            size="small"
            style={{ minWidth: 300, flex: "1 1 320px" }}
            value={pendingMembers}
            onChange={(v: number[]) => setPendingMembers(v)}
            options={messageOptions}
            placeholder="Select messages..."
            showSearch
            optionFilterProp="label"
          />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            size="small"
            type="primary"
            icon={editing ? <CheckOutlined /> : <PlusOutlined />}
            disabled={!pendingCategoryWide && pendingMembers.length === 0}
            onClick={handleAddOrUpdateGroup}
          >
            {editing ? "Update Entry" : "Add Group"}
          </Button>
          {editing && (
            <Button size="small" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {groups.length > 0 && (
        <List
          size="small"
          bordered
          dataSource={groups}
          renderItem={(group, gi) => {
            const catData = CATEGORY_DATA_BY_MSG_CAT.get(group.category);
            const catLabel = catData?.label ?? String(group.category);

            return (
              <List.Item
                style={{ alignItems: "flex-start" }}
                actions={
                  readOnly
                    ? []
                    : [
                        <Button
                          key="delete-group"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteGroup(gi)}
                        >
                          Delete
                        </Button>,
                      ]
                }
              >
                <div style={{ flex: 1 }}>
                  <Tag color="blue">{catLabel}</Tag>
                  <span
                    style={{
                      display: "inline-flex",
                      gap: 16,
                      alignItems: "center",
                      marginLeft: 8,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                      title="When ON (default), an incoming active=false does NOT evict any peers. When OFF, any incoming value evicts other configured values."
                    >
                      <Switch
                        size="small"
                        checked={group.evictOnlyWhenActive}
                        disabled={readOnly}
                        onChange={(v) => handleToggleEvictOnlyWhenActive(gi, v)}
                      />
                      Only active values evict
                    </span>
                    <span
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                      title="When ON, evictions emit an explicit active=false message downstream. When OFF (default), peers are silently removed from the state hashmap."
                    >
                      <Switch
                        size="small"
                        checked={group.emitDeactivations}
                        disabled={readOnly}
                        onChange={(v) => handleToggleEmitDeactivations(gi, v)}
                      />
                      Emit explicit deactivations
                    </span>
                  </span>
                  {group.categoryWide ? (
                    <span style={{ fontSize: 12, color: "#888" }}>
                      All {catLabel} messages are mutually exclusive
                    </span>
                  ) : (
                    <div style={{ marginTop: 4 }}>
                      {group.entries.map((entry, ei) => (
                        <div
                          key={ei}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            marginTop: 2,
                            background:
                              editing?.gi === gi && editing?.ei === ei
                                ? "rgba(24, 144, 255, 0.1)"
                                : undefined,
                            padding: "2px 4px",
                            borderRadius: 4,
                          }}
                        >
                          <span style={{ fontSize: 12 }}>Entry {ei + 1}:</span>
                          {entry.members.map((m, mi) => {
                            const ev = extractEnumValueFromMessageT(m);
                            return (
                              <Tag key={mi} style={{ fontSize: 11 }}>
                                {resolveEnumName(group.category, ev)}
                              </Tag>
                            );
                          })}
                          {!readOnly && (
                            <>
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditEntry(gi, ei)}
                              />
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteEntry(gi, ei)}
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}
